const spacesContent = {
  title: "Spaces",
  description: "Monitored folders that automatically organize your files using AI and a local Knowledge Graph.",
  sections: [
    {
      id: "overview",
      title: "What are Spaces?",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          <strong>Spaces</strong> are folders on your computer that Sortio watches continuously. Once a Space is set up, Sortio indexes every file inside it, extracts information into a local <strong>Knowledge Graph</strong>, and automatically sorts new files as they arrive — without any manual effort.
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          Think of a Space as a smart folder with memory. Drop a contract into your "Work" Space, and Sortio reads it, recognizes the client name, routes it to the right subfolder, and links it to every other file that mentions that client. Drop in a photo, and Sortio extracts the date and location from its metadata.
        </p>

        <div class="grid md:grid-cols-2 gap-4 my-8">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center">
              <span class="mr-2">📁</span> Monitored Folders
            </h4>
            <p class="text-[#424245]">A Space points at any folder you choose. Sortio watches that folder and keeps it organized.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center">
              <span class="mr-2">🤖</span> Auto-Sort
            </h4>
            <p class="text-[#424245]">New files are analyzed and moved to the right subfolder as soon as they appear.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center">
              <span class="mr-2">🧠</span> Knowledge Graph
            </h4>
            <p class="text-[#424245]">People, companies, locations, and documents are extracted and linked across your files.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center">
              <span class="mr-2">⚡</span> Rules Integration
            </h4>
            <p class="text-[#424245]">Bind deterministic <a href="/docs/rules-engine" class="text-[#06c]">Automations</a> to a Space for predictable behavior alongside AI.</p>
          </div>
        </div>
      `
    },
    {
      id: "creating-a-space",
      title: "Creating a Space",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Creating a Space takes a single click — there's no configuration wizard, no forms, just a folder picker.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">Step-by-Step</h4>
          </div>
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">1</span>
                </div>
                Open the Spaces page
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Click <strong>Spaces</strong> in the left sidebar.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">2</span>
                </div>
                Click "Add Space"
              </h5>
              <p class="mt-2 text-[#424245] ml-10">The button is in the top-right of the Spaces page. The native folder picker will open.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">3</span>
                </div>
                Pick any folder
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Choose a folder on your computer — Downloads, Documents, a project folder, or anything else. Click <strong>Open</strong>.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">4</span>
                </div>
                Indexing starts automatically
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Sortio scans every file in the folder, extracts entities, and builds the Knowledge Graph. Progress appears at the bottom of the app window.</p>
            </div>
          </div>
        </div>

        <div class="mt-8 border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">💡</span>
            Tip: Start with a focused folder
          </h4>
          <p class="text-[#424245]">Your first Space should be something contained — like a project folder or your Downloads. Large folders with thousands of files will work fine, but smaller Spaces finish indexing faster so you can see results sooner.</p>
        </div>
      `
    },
    {
      id: "space-detail-view",
      title: "The Space Detail View",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Click any Space in the grid to open its detail view. This is the dashboard for everything Sortio knows about the folder.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Stats at a glance</h4>
              <p class="text-[#424245]">Three cards show <strong>files indexed</strong>, <strong>entities extracted</strong>, and <strong>last activity</strong>. These update in real time as Sortio processes new files.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Top Entities</h4>
              <p class="text-[#424245]">The most-referenced entities across the Space, grouped by kind: <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">PERSON</code>, <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">ORG</code>, <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">LOCATION</code>, <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">DOC</code>, <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">DATE</code>, and more. Each pill shows how many files that entity appears in.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Recent Files</h4>
              <p class="text-[#424245]">The most recent activity in this Space — files that were indexed, sorted, or modified. Click any file to see the entities Sortio extracted from it.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Action buttons</h4>
              <ul class="list-disc list-inside text-[#424245] space-y-1 ml-2">
                <li><strong>Sort</strong> — opens the Sort page pre-filled with this folder for an on-demand AI sort.</li>
                <li><strong>Open Folder</strong> — reveals the Space's folder in Finder or Explorer.</li>
                <li><strong>Re-index</strong> — rescans every file in the Space. Useful after major changes or if entities look incomplete.</li>
                <li><strong>Live indexing on / Live indexing paused</strong> — toggles whether Sortio watches the folder for new changes. Click to pause or resume. Existing data stays intact either way.</li>
                <li><strong>Delete Space</strong> — removes the Space from Sortio. Your actual files and folder are never touched.</li>
              </ul>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "knowledge-graph",
      title: "The Knowledge Graph",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Every Space feeds into Sortio's local Knowledge Graph — a structured database of the <strong>entities</strong> found in your files and the <strong>relationships</strong> between them.
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          This is what makes Sortio smarter than a typical file organizer. Instead of just sorting by filename or file type, Sortio understands that "a contract from Acme Corp signed by Sarah Chen" and "an invoice from Acme Corp" are related — and can organize them together.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">Entity Kinds</h4>
          </div>
          <div class="p-6">
            <div class="grid sm:grid-cols-2 gap-3">
              <div class="flex items-center gap-3">
                <span class="inline-block px-2 py-1 rounded text-xs font-bold text-[#2563EB] bg-[#2563EB]/10">PERSON</span>
                <span class="text-[#424245] text-sm">People mentioned in your documents</span>
              </div>
              <div class="flex items-center gap-3">
                <span class="inline-block px-2 py-1 rounded text-xs font-bold text-[#B45309] bg-[#B45309]/10">ORG</span>
                <span class="text-[#424245] text-sm">Companies, institutions, agencies</span>
              </div>
              <div class="flex items-center gap-3">
                <span class="inline-block px-2 py-1 rounded text-xs font-bold text-[#16A34A] bg-[#16A34A]/10">DOC</span>
                <span class="text-[#424245] text-sm">Document types (contracts, reports, invoices)</span>
              </div>
              <div class="flex items-center gap-3">
                <span class="inline-block px-2 py-1 rounded text-xs font-bold text-[#DC2626] bg-[#DC2626]/10">AMOUNT</span>
                <span class="text-[#424245] text-sm">Financial figures and transaction amounts</span>
              </div>
              <div class="flex items-center gap-3">
                <span class="inline-block px-2 py-1 rounded text-xs font-bold text-[#0891B2] bg-[#0891B2]/10">LOCATION</span>
                <span class="text-[#424245] text-sm">Addresses, cities, geographic regions</span>
              </div>
              <div class="flex items-center gap-3">
                <span class="inline-block px-2 py-1 rounded text-xs font-bold text-[#7C3AED] bg-[#7C3AED]/10">TOPIC</span>
                <span class="text-[#424245] text-sm">Themes and subjects discussed in files</span>
              </div>
              <div class="flex items-center gap-3">
                <span class="inline-block px-2 py-1 rounded text-xs font-bold text-[#C2410C] bg-[#C2410C]/10">DATE</span>
                <span class="text-[#424245] text-sm">Significant dates from documents</span>
              </div>
            </div>
          </div>
        </div>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur p-6">
          <h4 class="font-semibold text-[#1d1d1f] mb-3">How entities improve sorting</h4>
          <p class="text-[#424245] mb-3">
            When you use the Sort feature on a KG-indexed folder, you'll see a green banner: <em>"Smart sorting enabled — Sortio has read X details from your files."</em>
          </p>
          <p class="text-[#424245]">
            This means Sortio is using semantic understanding, not just filenames. A file named <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">scan_0042.pdf</code> becomes sortable by the entities it contains — the client it's for, the project it belongs to, the date it was signed.
          </p>
        </div>
      `
    },
    {
      id: "live-indexing",
      title: "Live Indexing",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Once a Space is set up, Sortio watches the folder for changes in real time. New files are indexed automatically, modified files are re-scanned, and deleted files are cleaned up from the Knowledge Graph.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-4">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">What triggers a re-index</h4>
              <ul class="list-disc list-inside text-[#424245] space-y-1 ml-2">
                <li>A new file is added to the Space folder (or any subfolder)</li>
                <li>An existing file is modified</li>
                <li>A file is renamed or moved within the Space</li>
                <li>A file is deleted (its entities are removed from the graph)</li>
              </ul>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">How long does it take?</h4>
              <p class="text-[#424245]">Most files are processed within seconds of being added. Large PDFs or documents with deep content analysis may take longer. You can always check progress at the bottom of the app window.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Pausing live indexing</h4>
              <p class="text-[#424245]">Click the <strong>Live indexing on</strong> button in the Space detail view to toggle off live watching. The label changes to <strong>Live indexing paused</strong> — click it again to resume. This is useful if you're doing bulk operations you don't want Sortio to process yet.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Debounce</h4>
              <p class="text-[#424245]">File events are debounced by 2 seconds — meaning Sortio waits 2 seconds after the last change before processing. This prevents redundant work when multiple files change in rapid succession.</p>
            </div>
          </div>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">ℹ️</span>
            Large folders with many changes
          </h4>
          <p class="text-[#424245]">If you're expecting thousands of files to change at once (e.g. you're about to download a big archive), consider pausing live indexing first, doing the bulk operation, then clicking <strong>Re-index</strong> when you're done. This is faster than processing each change individually.</p>
        </div>
      `
    },
    {
      id: "rules-integration",
      title: "Integrating with Automations",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Spaces and <a href="/docs/rules-engine" class="text-[#06c]">Automations</a> (the Rules Engine) work together. A Space handles the AI side — extracting entities and watching for changes. Automations handle the deterministic side — exact, predictable rules like "any PDF with 'invoice' in the name moves to the Invoices subfolder."
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          You can assign one or more Automations to a Space. As the UI notes, automations run instantly and don't use your AI allowance at all: rules that match run first, and anything that doesn't match a rule falls through to AI-powered sorting.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">Assigning Automations to a Space</h4>
          </div>
          <div class="p-6 space-y-3">
            <p class="text-[#424245]">1. Open the Space detail view</p>
            <p class="text-[#424245]">2. Scroll to the <strong>Automations</strong> section</p>
            <p class="text-[#424245]">3. Click <strong>Edit</strong> and check the rules you want to attach</p>
            <p class="text-[#424245]">4. Click <strong>Done</strong> — the rules are now bound to this Space</p>
          </div>
        </div>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">🧠</span>
            Knowledge Graph + Automation templates
          </h4>
          <p class="text-[#424245] mb-3">Automations can reference entities from the Knowledge Graph using template variables. The syntax is <code class="bg-white px-2 py-0.5 rounded text-sm">{entity.&lt;kind&gt;}</code>, where kind is one of the entity types supported by the Knowledge Graph:</p>
          <ul class="list-disc list-inside text-[#424245] space-y-1 ml-2 mb-3">
            <li><code class="bg-white px-2 py-0.5 rounded text-sm">{entity.person}</code> — the primary person mentioned in the file</li>
            <li><code class="bg-white px-2 py-0.5 rounded text-sm">{entity.organization}</code> — the primary company or institution (alias: <code class="bg-white px-2 py-0.5 rounded text-sm">{entity.company}</code>)</li>
            <li><code class="bg-white px-2 py-0.5 rounded text-sm">{entity.document}</code> — the document type, e.g. Contract, Invoice (alias: <code class="bg-white px-2 py-0.5 rounded text-sm">{entity.doc}</code>)</li>
            <li><code class="bg-white px-2 py-0.5 rounded text-sm">{entity.financial}</code> — an amount or transaction figure</li>
            <li><code class="bg-white px-2 py-0.5 rounded text-sm">{entity.date}</code>, <code class="bg-white px-2 py-0.5 rounded text-sm">{entity.location}</code>, <code class="bg-white px-2 py-0.5 rounded text-sm">{entity.topic}</code></li>
          </ul>
          <p class="text-[#424245]">You can provide a fallback value with the pipe syntax: <code class="bg-white px-2 py-0.5 rounded text-sm">{entity.organization|Unsorted}</code>. This lets you build rules that move files into folders named after the people or companies they mention — with no hard-coded names needed.</p>
        </div>
      `
    },
    {
      id: "best-practices",
      title: "Best Practices",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-6">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Start with one Space at a time</h4>
              <p class="text-[#424245]">Set up your first Space on a folder you actively use, like Downloads or a current project. Get comfortable with how it behaves before adding more.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Back up before enabling auto-sort</h4>
              <p class="text-[#424245]">Sortio has multiple safety layers (backups, atomic operations, undo) but we recommend backing up any critical folder before turning on live indexing and auto-sorting.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Combine with a local AI model for privacy</h4>
              <p class="text-[#424245]">If the files in your Space are sensitive (legal, medical, financial), switch to a local <a href="/docs/local-models" class="text-[#06c]">Ollama model</a>. Your documents stay on your machine — no data is ever sent to any server.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Re-index after major reorganizations</h4>
              <p class="text-[#424245]">If you've manually moved or renamed a lot of files, trigger a <strong>Re-index</strong> to ensure the Knowledge Graph reflects the current state of the folder.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-6">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Indexing seems stuck</h4>
              <p class="text-[#424245]">Check the status bar at the bottom of the app. If it's processing a large file, give it a minute. If it truly appears stuck, try clicking <strong>Pause</strong> then <strong>Re-index</strong> to restart the process.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Entities look wrong or missing</h4>
              <p class="text-[#424245]">Extraction quality depends on the AI model you're using. If you're on a smaller local model, try switching to a larger one (Llama 3.3, DeepSeek) or to Sortio Cloud for better results. Then re-index the Space.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">New files aren't being sorted</h4>
              <p class="text-[#424245]">Check that the Space's status badge shows <strong>Active</strong> (not <strong>Paused</strong>). If it's paused, click the <strong>Live indexing paused</strong> button to resume. Also verify that your Automation rules are bound to the Space if you're relying on them.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">I deleted a Space by accident</h4>
              <p class="text-[#424245]">Your files are safe — deleting a Space only removes it from Sortio, never from your disk. You can recreate the Space by clicking <strong>Add Space</strong> and picking the same folder again. Re-indexing will restore the Knowledge Graph.</p>
            </div>
          </div>
        </div>
      `
    }
  ]
};

export default spacesContent;
